import React, { useEffect, useState } from "react";
import {
  Typography,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  Tooltip,
  Collapse,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Grow,
  Radio,
  RadioGroup,
} from "@material-ui/core";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import ReceiptOutlinedIcon from "@material-ui/icons/ReceiptOutlined";
import ReceiptLongOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import NotificationsActiveOutlinedIcon from "@material-ui/icons/NotificationsActiveOutlined";
import RestaurantMenuOutlinedIcon from "@material-ui/icons/RestaurantMenuOutlined";
import AccessTimeOutlinedIcon from "@material-ui/icons/AccessTimeOutlined";
import InventoryOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import MonetizationOnOutlinedIcon from "@material-ui/icons/MonetizationOnOutlined";
import TuneOutlinedIcon from "@material-ui/icons/TuneOutlined";
import ListAltOutlinedIcon from "@material-ui/icons/ListAltOutlined";
import AssignmentReturnOutlinedIcon from "@material-ui/icons/AssignmentReturnOutlined";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import EventOutlinedIcon from "@material-ui/icons/EventOutlined";
import StraightenOutlinedIcon from "@material-ui/icons/StraightenOutlined";
import CloseIcon from "@material-ui/icons/Close";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import * as api from "../../api/index";
import { useSnackbar } from "../../utils/useSnackbar";
import cardStyles from "../Settings/SettingsCard.module.css";
import { CardSkeleton } from "../Skeletons";
import ReportVisibilitySettings from "../../admin/ReportVisibilitySettings";
import { getCurrencySymbol, applyCurrencyRounding } from "../../utils/currency";

// Same "view card + Edit opens a popup dialog" convention every per-store
// Settings card already uses (ShiftSettings.js/InvoiceFormatSettings.js) -
// this panel used to put every switch directly on the card face, which was
// the odd one out. One shared shell renders all 6 cards; each caller only
// supplies the read-only summary and the dialog's own controls.
const StatusPill = ({ on, onLabel, offLabel }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 7,
      padding: "5px 14px 5px 10px",
      borderRadius: 9999,
      fontWeight: 700,
      fontSize: "0.82rem",
      letterSpacing: "0.01em",
      backgroundColor: on ? "#f0fdf4" : "#fef2f2",
      border: `1.5px solid ${on ? "#86efac" : "#fca5a5"}`,
      color: on ? "#15803d" : "#b91c1c",
      boxShadow: on
        ? "0 2px 8px rgba(22, 163, 74, 0.15), inset 0 1px 0 rgba(255,255,255,0.8)"
        : "0 2px 8px rgba(220, 38, 38, 0.12), inset 0 1px 0 rgba(255,255,255,0.8)",
      transition: "all 0.22s ease",
    }}
  >
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: on ? "#16a34a" : "#dc2626",
        display: "inline-block",
        flexShrink: 0,
        boxShadow: on
          ? "0 0 0 3px rgba(34, 197, 94, 0.22)"
          : "0 0 0 3px rgba(239, 68, 68, 0.18)",
        animation: on ? "pillPulse 2.2s ease-in-out infinite" : "none",
      }}
    />
    {on ? onLabel : offLabel}
  </span>
);


const SettingCard = ({
  icon: Icon,
  iconVariant,
  title,
  statOn,
  statOnLabel,
  statOffLabel,
  hint,
  editing,
  onEdit,
  onCancel,
  onSave,
  saving,
  children,
}) => (
  <div className={`${cardStyles.card} ${statOn ? cardStyles.cardActive : cardStyles.cardDisabled}`}>
    <div className={cardStyles.cardHeader}>
      <span className={`${cardStyles.cardIcon} ${cardStyles[iconVariant]}`}>
        <Icon fontSize="small" />
      </span>
      <Typography className={cardStyles.cardTitle}>{title}</Typography>
    </div>

    <div className={cardStyles.cardBody}>
      <div className={cardStyles.statBlock}>
        <div className={cardStyles.statValueRow}>
          <StatusPill on={statOn} onLabel={statOnLabel} offLabel={statOffLabel} />
        </div>
        <span className={cardStyles.statHint}>{hint}</span>
      </div>
    </div>

    <div className={cardStyles.cardActions}>
      <Button
        className={cardStyles.editButton}
        disableElevation
        onClick={onEdit}
        startIcon={<EditOutlinedIcon style={{ fontSize: 15 }} />}
      >
        Edit
      </Button>
    </div>

    <Dialog
      open={editing}
      onClose={onCancel}
      fullWidth
      maxWidth="xs"
      TransitionComponent={Grow}
      BackdropProps={{ style: { backdropFilter: "blur(3px)", backgroundColor: "rgba(15,23,42,0.35)" } }}
      PaperProps={{
        style: { borderRadius: 20, overflow: "hidden", boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.25)" },
      }}
    >
      <div className={cardStyles.dialogHeader}>
        <div className={cardStyles.dialogHeaderLeft}>
          <Icon />
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            {title}
          </Typography>
        </div>
        <IconButton size="small" className={cardStyles.dialogCloseBtn} onClick={onCancel}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <DialogContent className={cardStyles.dialogBody}>{children}</DialogContent>

      <DialogActions className={cardStyles.dialogActions}>
        <Button className={cardStyles.saveBtn} disableElevation onClick={onSave} disabled={saving}>
          {saving ? "Saving…" : "Save Settings"}
        </Button>
        <Button className={cardStyles.cancelBtn} disableElevation onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  </div>
);

const DEFAULTS = {
  customInvoiceFormatEntitled: false,
  useCustomInvoiceFormat: false,
  enableA4InvoiceView: true,
  showExpiryBadge: true,
  expiryReminderDays: 7,
  enableHoldKot: false,
  holdKotEntitled: true,
  enableShiftTracking: false,
  shiftTrackingEntitled: true,
  showStock: false,
  currencySettingsEntitled: true,
  defaultCurrency: "THB",
  activityLogsVisible: true,
  goodsReturnEntitled: true,
  roundingRules: { THB: 0.5, INR: 0.5 },
  programLogRetentionDays: 30,
  // Special Features - default OFF (see server/models/GlobalSettingsModel.js).
  lockItemPriceOverrides: false,
  minSaleQuantityEnabled: false,
  backDateInvoiceEntitled: false,
  backDateInvoiceMaxDays: 7,
};

// Presets shown as quick-pick chips - any other whole number (1-1825, same
// bound the server enforces) can still be typed into the "Custom" field
// next to them.
const RETENTION_DAY_PRESETS = [30, 60, 90, 120, 150, 200];

const CURRENCY_LABELS = { THB: "Thai Baht (฿)", INR: "Indian Rupee (₹)" };

// Fixed set of picks, deliberately - the client hands a validated increment
// to the server (server/controllers/globalSettings.js rejects anything else
// too), so an operator can never fat-finger a rounding step that distorts
// every sale total for a currency. "Off" (0) keeps standard 2-decimal
// precision, same as today.
const ROUNDING_INCREMENT_OPTIONS = [
  { value: 0, label: "Off" },
  { value: 0.25, label: "0.25" },
  { value: 0.5, label: "0.50" },
  { value: 1, label: "1.00" },
];

// Chosen so the live example under each picker demonstrably shows what that
// specific increment does, not just "rounding happened": .63's nearest
// quarter-step is .75 (0.25 rule), its nearest half-step is .50 - not a
// whole number (0.50 rule, so it doesn't look identical to the 1.00 rule),
// and it's past the halfway point to the next whole number (1.00 rule
// rounds up). 160.83 used to sit here, but its 0.50 example happened to
// land on a whole number (161.00), which read as "0.50 rounds to whole
// numbers" - not true in general, and not what that rule is for.
const ROUNDING_EXAMPLE_AMOUNT = 160.63;

const GlobalSettingsTab = () => {
  const [openSnackbar] = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [purging, setPurging] = useState(false);
  const [settings, setSettings] = useState(DEFAULTS);
  const [draft, setDraft] = useState(DEFAULTS);
  const [editingCard, setEditingCard] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.getGlobalSettings();
        const s = { ...DEFAULTS, ...(data?.data || {}) };
        setSettings(s);
      } catch (error) {
        openSnackbar("Failed to load global settings");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEdit = (card) => {
    setDraft(settings);
    setEditingCard(card);
  };
  const closeEdit = () => setEditingCard(null);
  const setDraftField = (field) => (value) => setDraft((prev) => ({ ...prev, [field]: value }));
  const setRoundingIncrement = (code) => (value) =>
    setDraft((prev) => ({ ...prev, roundingRules: { ...prev.roundingRules, [code]: Number(value) } }));

  const handlePurgeNow = async () => {
    const days = Number(draft.programLogRetentionDays) || 30;
    if (
      !window.confirm(
        `This permanently deletes every Activity Log row older than ${days} day(s), for every store. This cannot be undone. Continue?`
      )
    ) {
      return;
    }
    setPurging(true);
    try {
      // Purge always reads the retention window from the saved settings, not
      // from this dialog's draft - persist it first so a value the admin just
      // picked but hasn't hit "Save Settings" for yet is actually what gets
      // applied, instead of silently purging against the old window.
      if (days !== Number(settings.programLogRetentionDays)) {
        const { data: updated } = await api.updateGlobalSettings({ programLogRetentionDays: days });
        setSettings((prev) => ({ ...prev, ...(updated?.data || { programLogRetentionDays: days }) }));
      }
      const { data } = await api.purgeProgramLogs();
      // The server caps how much it deletes per request (so this button
      // never sits spinning for minutes on a huge backlog) - truncated means
      // there's more past the window than one click cleared.
      const suffix = data?.truncated ? " - more remain, click Purge again to continue" : "";
      openSnackbar(`Deleted ${data?.deletedCount ?? 0} log(s) older than ${data?.retentionDays ?? days} day(s)${suffix}`);
    } catch (error) {
      openSnackbar(error?.response?.data?.message || "Failed to purge old logs");
    } finally {
      setPurging(false);
    }
  };

  const save = async (payload, successMessage) => {
    setSaving(true);
    try {
      const { data } = await api.updateGlobalSettings(payload);
      setSettings((prev) => ({ ...prev, ...(data?.data || payload) }));
      openSnackbar(successMessage);
      closeEdit();
    } catch (error) {
      openSnackbar(error?.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
        <CardSkeleton />
        <CardSkeleton />
        <CardSkeleton />
      </div>
    );
  }

  return (
    <>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20, alignItems: "start" }}>
      <SettingCard
        icon={ReceiptOutlinedIcon}
        iconVariant="cardIconBlue"
        title="Custom Invoice Format"
        statOn={settings.customInvoiceFormatEntitled && settings.useCustomInvoiceFormat}
        statOnLabel="Active for every store"
        statOffLabel={settings.customInvoiceFormatEntitled ? "Entitled, not active" : "Disabled entirely"}
        hint="Controls whether stores can use a bespoke A4 invoice layout."
        editing={editingCard === "format"}
        onEdit={() => openEdit("format")}
        onCancel={closeEdit}
        saving={saving}
        onSave={() =>
          save(
            { customInvoiceFormatEntitled: draft.customInvoiceFormatEntitled, useCustomInvoiceFormat: draft.customInvoiceFormatEntitled ? draft.useCustomInvoiceFormat : false },
            "Custom invoice format settings updated for every store"
          )
        }
      >
        <div className={cardStyles.dialogSection}>
          <Typography className={cardStyles.dialogSectionTitle}>Entitlement</Typography>
          <div className={cardStyles.switchRow} style={{ borderColor: draft.customInvoiceFormatEntitled ? "#fed7aa" : "#e2e8f0", backgroundColor: draft.customInvoiceFormatEntitled ? "#fffaf5" : "#f8fafc" }}>
            <span className={cardStyles.switchLabel}>Entitled (feature exists at all)</span>
            <FormControlLabel style={{ margin: 0 }} label="" control={<Switch size="small" color="primary" checked={draft.customInvoiceFormatEntitled} onChange={(e) => setDraftField("customInvoiceFormatEntitled")(e.target.checked)} disabled={saving} />} />
          </div>
          <Collapse in={draft.customInvoiceFormatEntitled} timeout={220}>
            <div className={cardStyles.switchRow} style={{ marginTop: 10, borderColor: draft.useCustomInvoiceFormat ? "#fed7aa" : "#e2e8f0", backgroundColor: draft.useCustomInvoiceFormat ? "#fffaf5" : "#f8fafc" }}>
              <span className={cardStyles.switchLabel}>Active by default for every store</span>
              <FormControlLabel style={{ margin: 0 }} label="" control={<Switch size="small" color="primary" checked={draft.useCustomInvoiceFormat} onChange={(e) => setDraftField("useCustomInvoiceFormat")(e.target.checked)} disabled={saving} />} />
            </div>
          </Collapse>
        </div>
      </SettingCard>

      <SettingCard
        icon={ReceiptLongOutlinedIcon}
        iconVariant="cardIconTeal"
        title="A4 Invoice View"
        statOn={settings.enableA4InvoiceView}
        statOnLabel="A4 view available"
        statOffLabel="Thermal only"
        hint="Whether the Thermal/A4 switch is shown at all, for every store."
        editing={editingCard === "a4"}
        onEdit={() => openEdit("a4")}
        onCancel={closeEdit}
        saving={saving}
        onSave={() => save({ enableA4InvoiceView: draft.enableA4InvoiceView }, "A4 invoice view setting updated for every store")}
      >
        <div className={cardStyles.dialogSection}>
          <Typography className={cardStyles.dialogSectionTitle}>A4 View Availability</Typography>
          <div className={cardStyles.switchRow} style={{ borderColor: draft.enableA4InvoiceView ? "#fed7aa" : "#e2e8f0", backgroundColor: draft.enableA4InvoiceView ? "#fffaf5" : "#f8fafc" }}>
            <span className={cardStyles.switchLabel}>{draft.enableA4InvoiceView ? "A4 view available" : "Thermal only"}</span>
            <FormControlLabel style={{ margin: 0 }} label="" control={<Switch size="small" color="primary" checked={draft.enableA4InvoiceView} onChange={(e) => setDraftField("enableA4InvoiceView")(e.target.checked)} disabled={saving} />} />
          </div>
        </div>
      </SettingCard>

      <SettingCard
        icon={NotificationsActiveOutlinedIcon}
        iconVariant="cardIconRose"
        title="Expiry Reminder"
        statOn={settings.showExpiryBadge}
        statOnLabel={`Badge shown (${settings.expiryReminderDays}d)`}
        statOffLabel="Badge hidden"
        hint="Whether an 'expiring soon' badge shows on the login screen."
        editing={editingCard === "reminder"}
        onEdit={() => openEdit("reminder")}
        onCancel={closeEdit}
        saving={saving}
        onSave={() => {
          const days = Number(draft.expiryReminderDays);
          if (!Number.isFinite(days) || days < 0) {
            openSnackbar("Reminder days must be a non-negative number");
            return;
          }
          save({ showExpiryBadge: draft.showExpiryBadge, expiryReminderDays: days }, "Expiry reminder settings updated");
        }}
      >
        <div className={cardStyles.dialogSection}>
          <Typography className={cardStyles.dialogSectionTitle}>Login Reminder Badge</Typography>
          <div className={cardStyles.switchRow} style={{ borderColor: draft.showExpiryBadge ? "#fed7aa" : "#e2e8f0", backgroundColor: draft.showExpiryBadge ? "#fffaf5" : "#f8fafc" }}>
            <span className={cardStyles.switchLabel}>{draft.showExpiryBadge ? "Badge shown" : "Badge hidden"}</span>
            <FormControlLabel style={{ margin: 0 }} label="" control={<Switch size="small" color="primary" checked={draft.showExpiryBadge} onChange={(e) => setDraftField("showExpiryBadge")(e.target.checked)} disabled={saving} />} />
          </div>
          <Collapse in={draft.showExpiryBadge} timeout={220}>
            <TextField
              label="Days before expiry"
              type="number"
              size="small"
              variant="outlined"
              value={draft.expiryReminderDays}
              onChange={(e) => setDraftField("expiryReminderDays")(e.target.value)}
              disabled={saving}
              inputProps={{ min: 0 }}
              style={{ marginTop: 10 }}
              fullWidth
            />
          </Collapse>
        </div>
      </SettingCard>

      <SettingCard
        icon={RestaurantMenuOutlinedIcon}
        iconVariant="cardIconPurple"
        title="Hold KOT"
        statOn={settings.holdKotEntitled && settings.enableHoldKot}
        statOnLabel="Active by default"
        statOffLabel={settings.holdKotEntitled ? "Entitled, not active" : "Disabled entirely"}
        hint="Controls whether stores can hold a kitchen order ticket before sending it."
        editing={editingCard === "holdkot"}
        onEdit={() => openEdit("holdkot")}
        onCancel={closeEdit}
        saving={saving}
        onSave={() =>
          save(
            { holdKotEntitled: draft.holdKotEntitled, enableHoldKot: draft.holdKotEntitled ? draft.enableHoldKot : false },
            "Hold KOT setting updated for every store"
          )
        }
      >
        <div className={cardStyles.dialogSection}>
          <Typography className={cardStyles.dialogSectionTitle}>Entitlement</Typography>
          <div className={cardStyles.switchRow} style={{ borderColor: draft.holdKotEntitled ? "#fed7aa" : "#e2e8f0", backgroundColor: draft.holdKotEntitled ? "#fffaf5" : "#f8fafc" }}>
            <span className={cardStyles.switchLabel}>Entitled (feature exists at all)</span>
            <FormControlLabel style={{ margin: 0 }} label="" control={<Switch size="small" color="primary" checked={draft.holdKotEntitled} onChange={(e) => setDraftField("holdKotEntitled")(e.target.checked)} disabled={saving} />} />
          </div>
          <Collapse in={draft.holdKotEntitled} timeout={220}>
            <div className={cardStyles.switchRow} style={{ marginTop: 10, borderColor: draft.enableHoldKot ? "#fed7aa" : "#e2e8f0", backgroundColor: draft.enableHoldKot ? "#fffaf5" : "#f8fafc" }}>
              <span className={cardStyles.switchLabel}>Active by default for every store</span>
              <FormControlLabel style={{ margin: 0 }} label="" control={<Switch size="small" color="primary" checked={draft.enableHoldKot} onChange={(e) => setDraftField("enableHoldKot")(e.target.checked)} disabled={saving} />} />
            </div>
          </Collapse>
          {!draft.holdKotEntitled && (
            <Typography variant="caption" style={{ color: "#92400e", display: "block", marginTop: 10, lineHeight: 1.5 }}>
              ⚠️ Turning off entitlement hides the Hold KOT card in every store's own Settings immediately - only SuperAdmin can bring it back.
            </Typography>
          )}
        </div>
      </SettingCard>

      <SettingCard
        icon={AccessTimeOutlinedIcon}
        iconVariant="cardIconBlue"
        title="Shift Tracking"
        statOn={settings.shiftTrackingEntitled && settings.enableShiftTracking}
        statOnLabel="Active by default"
        statOffLabel={settings.shiftTrackingEntitled ? "Entitled, not active" : "Disabled entirely"}
        hint="Employee Master, Shift Logs, and the DSR employee filter all follow this too."
        editing={editingCard === "shift"}
        onEdit={() => openEdit("shift")}
        onCancel={closeEdit}
        saving={saving}
        onSave={() =>
          save(
            { shiftTrackingEntitled: draft.shiftTrackingEntitled, enableShiftTracking: draft.shiftTrackingEntitled ? draft.enableShiftTracking : false },
            "Shift tracking setting updated for every store"
          )
        }
      >
        <div className={cardStyles.dialogSection}>
          <Typography className={cardStyles.dialogSectionTitle}>Entitlement</Typography>
          <div className={cardStyles.switchRow} style={{ borderColor: draft.shiftTrackingEntitled ? "#fed7aa" : "#e2e8f0", backgroundColor: draft.shiftTrackingEntitled ? "#fffaf5" : "#f8fafc" }}>
            <span className={cardStyles.switchLabel}>Entitled (feature exists at all)</span>
            <FormControlLabel style={{ margin: 0 }} label="" control={<Switch size="small" color="primary" checked={draft.shiftTrackingEntitled} onChange={(e) => setDraftField("shiftTrackingEntitled")(e.target.checked)} disabled={saving} />} />
          </div>
          <Collapse in={draft.shiftTrackingEntitled} timeout={220}>
            <div className={cardStyles.switchRow} style={{ marginTop: 10, borderColor: draft.enableShiftTracking ? "#fed7aa" : "#e2e8f0", backgroundColor: draft.enableShiftTracking ? "#fffaf5" : "#f8fafc" }}>
              <span className={cardStyles.switchLabel}>Active by default for every store</span>
              <FormControlLabel style={{ margin: 0 }} label="" control={<Switch size="small" color="primary" checked={draft.enableShiftTracking} onChange={(e) => setDraftField("enableShiftTracking")(e.target.checked)} disabled={saving} />} />
            </div>
          </Collapse>
          {!draft.shiftTrackingEntitled && (
            <Typography variant="caption" style={{ color: "#92400e", display: "block", marginTop: 10, lineHeight: 1.5 }}>
              ⚠️ Turning off entitlement hides Employee Master, Shift Logs, the DSR employee filter, and the Shift Management Settings card for every store.
            </Typography>
          )}
        </div>
      </SettingCard>

      <SettingCard
        icon={AssignmentReturnOutlinedIcon}
        iconVariant="cardIconTeal"
        title="Goods Return"
        statOn={settings.goodsReturnEntitled}
        statOnLabel="Feature available"
        statOffLabel="Disabled entirely"
        hint="Controls whether stores can create/manage goods returns at all - nav link, quick-action button, and invoice-level return icons."
        editing={editingCard === "goodsReturn"}
        onEdit={() => openEdit("goodsReturn")}
        onCancel={closeEdit}
        saving={saving}
        onSave={() => save({ goodsReturnEntitled: draft.goodsReturnEntitled }, "Goods Return setting updated for every store")}
      >
        <div className={cardStyles.dialogSection}>
          <Typography className={cardStyles.dialogSectionTitle}>Entitlement</Typography>
          <div className={cardStyles.switchRow} style={{ borderColor: draft.goodsReturnEntitled ? "#fed7aa" : "#e2e8f0", backgroundColor: draft.goodsReturnEntitled ? "#fffaf5" : "#f8fafc" }}>
            <span className={cardStyles.switchLabel}>Entitled (feature exists at all)</span>
            <FormControlLabel style={{ margin: 0 }} label="" control={<Switch size="small" color="primary" checked={draft.goodsReturnEntitled} onChange={(e) => setDraftField("goodsReturnEntitled")(e.target.checked)} disabled={saving} />} />
          </div>
          {!draft.goodsReturnEntitled && (
            <Typography variant="caption" style={{ color: "#92400e", display: "block", marginTop: 10, lineHeight: 1.5 }}>
              ⚠️ Turning off hides the Goods Returns nav link, Fab quick action, PoS cart-header icon, the Invoices list Return column/create-return icon, and the HowItWorks tile - for every store immediately.
            </Typography>
          )}
        </div>
      </SettingCard>

      <SettingCard
        icon={InventoryOutlinedIcon}
        iconVariant="cardIconTeal"
        title="Stock Visibility"
        statOn={settings.showStock}
        statOnLabel="Stock visible"
        statOffLabel="Stock hidden"
        hint="Whether every store can see live stock quantities in the PoS and item screens."
        editing={editingCard === "stock"}
        onEdit={() => openEdit("stock")}
        onCancel={closeEdit}
        saving={saving}
        onSave={() => save({ showStock: draft.showStock }, "Stock visibility setting updated for every store")}
      >
        <div className={cardStyles.dialogSection}>
          <Typography className={cardStyles.dialogSectionTitle}>Stock Visibility</Typography>
          <div className={cardStyles.switchRow} style={{ borderColor: draft.showStock ? "#fed7aa" : "#e2e8f0", backgroundColor: draft.showStock ? "#fffaf5" : "#f8fafc" }}>
            <span className={cardStyles.switchLabel}>{draft.showStock ? "Stock visible" : "Stock hidden"}</span>
            <FormControlLabel style={{ margin: 0 }} label="" control={<Switch size="small" color="primary" checked={draft.showStock} onChange={(e) => setDraftField("showStock")(e.target.checked)} disabled={saving} />} />
          </div>
        </div>
      </SettingCard>

      <SettingCard
        icon={MonetizationOnOutlinedIcon}
        iconVariant="cardIconRose"
        title="Currency Settings"
        statOn={settings.currencySettingsEntitled}
        statOnLabel={`Entitled - default ${settings.defaultCurrency}`}
        statOffLabel="Disabled entirely"
        hint="Controls whether stores can pick their own currency, and sets the deployment-wide default."
        editing={editingCard === "currency"}
        onEdit={() => openEdit("currency")}
        onCancel={closeEdit}
        saving={saving}
        onSave={() => save({ currencySettingsEntitled: draft.currencySettingsEntitled, defaultCurrency: draft.defaultCurrency }, "Currency settings updated for every store")}
      >
        <div className={cardStyles.dialogSection}>
          <Typography className={cardStyles.dialogSectionTitle}>Entitlement</Typography>
          <div className={cardStyles.switchRow} style={{ borderColor: draft.currencySettingsEntitled ? "#fed7aa" : "#e2e8f0", backgroundColor: draft.currencySettingsEntitled ? "#fffaf5" : "#f8fafc" }}>
            <span className={cardStyles.switchLabel}>Entitled (feature exists at all)</span>
            <FormControlLabel style={{ margin: 0 }} label="" control={<Switch size="small" color="primary" checked={draft.currencySettingsEntitled} onChange={(e) => setDraftField("currencySettingsEntitled")(e.target.checked)} disabled={saving} />} />
          </div>
          {!draft.currencySettingsEntitled && (
            <Typography variant="caption" style={{ color: "#92400e", display: "block", marginTop: 10, lineHeight: 1.5 }}>
              ⚠️ Turning off entitlement hides the Currency Settings card for every store immediately - only SuperAdmin can bring it back.
            </Typography>
          )}
        </div>
        <div className={cardStyles.dialogSection}>
          <Typography className={cardStyles.dialogSectionTitle}>Deployment-wide Default</Typography>
          <RadioGroup
            value={draft.defaultCurrency}
            onChange={(e) => setDraftField("defaultCurrency")(e.target.value)}
          >
            {Object.entries(CURRENCY_LABELS).map(([code, label]) => (
              <FormControlLabel
                key={code}
                value={code}
                control={<Radio size="small" style={{ color: "#ea580c" }} disabled={saving} />}
                label={label}
              />
            ))}
          </RadioGroup>
          <Typography variant="caption" style={{ color: "#94a3b8", display: "block", marginTop: 4 }}>
            Applied to every store the moment you save - stores can still change their own afterward, if entitled.
          </Typography>
        </div>
      </SettingCard>

      <SettingCard
        icon={TuneOutlinedIcon}
        iconVariant="cardIconTeal"
        title="Currency Rounding"
        statOn={Object.values(settings.roundingRules || {}).some((v) => v > 0)}
        statOnLabel={Object.keys(CURRENCY_LABELS)
          .map((code) => {
            const inc = settings.roundingRules?.[code] || 0;
            return `${code} ${inc > 0 ? `→ nearest ${inc.toFixed(2)}` : "standard"}`;
          })
          .join("  ·  ")}
        statOffLabel="Standard 2-decimal for all currencies"
        hint="Rounds only the final POS sale total, once, after tax/discount/service charge - never Purchase or Stock Adjustment totals."
        editing={editingCard === "rounding"}
        onEdit={() => openEdit("rounding")}
        onCancel={closeEdit}
        saving={saving}
        onSave={() => save({ roundingRules: draft.roundingRules }, "Currency rounding rules updated for every store")}
      >
        <div className={cardStyles.dialogSection}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Typography className={cardStyles.dialogSectionTitle} style={{ margin: 0 }}>
              Rounding Increment
            </Typography>
            <Tooltip
              arrow
              title="Coins/notes don't come in every cent, so a customer's final payable amount is rounded to the nearest step here (e.g. Thai coins are 0.25฿ apart). Applied exactly once, to the POS sale total, after tax, discount and service charge are already included - it never touches Purchase or Stock Adjustment totals, which must stay exact to match supplier invoices and inventory valuation. 'Off' keeps the usual 2-decimal amount."
            >
              <InfoOutlinedIcon style={{ fontSize: 16, color: "#94a3b8", cursor: "help" }} />
            </Tooltip>
          </div>
          <Typography variant="body2" style={{ color: "#64748b", fontSize: "0.82rem", marginTop: 2, marginBottom: 14 }}>
            Pick how each currency's final payable total should land. This is a global rule for every store - not per-store.
          </Typography>

          {Object.entries(CURRENCY_LABELS).map(([code, label]) => {
            const increment = draft.roundingRules?.[code] ?? 0;
            const symbol = getCurrencySymbol(code);
            const { rounded, roundOff } = applyCurrencyRounding(
              ROUNDING_EXAMPLE_AMOUNT,
              code,
              draft.roundingRules
            );
            return (
              <div key={code} style={{ marginTop: 16 }}>
                <Typography variant="body2" style={{ fontWeight: 700, color: "#334155", marginBottom: 6, fontSize: "0.82rem" }}>
                  {label}
                </Typography>
                <div
                  style={{
                    background: "#f1f5f9",
                    padding: "4px",
                    borderRadius: "12px",
                    display: "flex",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <ToggleButtonGroup
                    value={increment}
                    exclusive
                    onChange={(e, val) => val != null && setRoundingIncrement(code)(val)}
                    size="small"
                    style={{ width: "100%", display: "flex" }}
                  >
                    {ROUNDING_INCREMENT_OPTIONS.map((opt) => (
                      <ToggleButton
                        key={opt.value}
                        value={opt.value}
                        disabled={saving}
                        style={{
                          flex: 1,
                          borderRadius: "9px",
                          padding: "7px 10px",
                          fontWeight: 700,
                          fontSize: "0.8rem",
                          textTransform: "none",
                          border: "none",
                          backgroundColor: increment === opt.value ? "#ffffff" : "transparent",
                          color: increment === opt.value ? "#ea580c" : "#64748b",
                          boxShadow: increment === opt.value ? "0 2px 6px rgba(0,0,0,0.06)" : "none",
                        }}
                      >
                        {opt.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </div>
                <Typography variant="caption" style={{ color: "#94a3b8", display: "block", marginTop: 6 }}>
                  {increment > 0 ? (
                    <>
                      Example: {symbol}{ROUNDING_EXAMPLE_AMOUNT.toFixed(2)} → <strong style={{ color: "#334155" }}>{symbol}{rounded.toFixed(2)}</strong>
                      {roundOff !== 0 && ` (round off ${roundOff > 0 ? "+" : ""}${symbol}${roundOff.toFixed(2)})`}
                    </>
                  ) : (
                    <>Example: {symbol}{ROUNDING_EXAMPLE_AMOUNT.toFixed(2)} stays {symbol}{ROUNDING_EXAMPLE_AMOUNT.toFixed(2)} - standard 2-decimal.</>
                  )}
                </Typography>
              </div>
            );
          })}

          <Typography variant="caption" style={{ color: "#94a3b8", display: "block", marginTop: 14, lineHeight: 1.5 }}>
            Applied to every store's POS checkout the moment you save. The rounding adjustment (if any) is shown to staff as its own "Round off" line and stored on the invoice for reporting.
          </Typography>
        </div>
      </SettingCard>

      <SettingCard
        icon={ListAltOutlinedIcon}
        iconVariant="cardIconPurple"
        title="Activity Logs"
        statOn={settings.activityLogsVisible}
        statOnLabel={`Visible · ${settings.programLogRetentionDays || 30}d retention`}
        statOffLabel="Hidden entirely"
        hint="Whether the Activity Logs page is shown, and how long log rows are kept before being auto-deleted."
        editing={editingCard === "activityLogs"}
        onEdit={() => openEdit("activityLogs")}
        onCancel={closeEdit}
        saving={saving}
        onSave={() => {
          const days = Number(draft.programLogRetentionDays);
          if (!Number.isInteger(days) || days < 1 || days > 1825) {
            openSnackbar("Retention must be a whole number of days between 1 and 1825");
            return;
          }
          save(
            { activityLogsVisible: draft.activityLogsVisible, programLogRetentionDays: days },
            "Activity Logs settings updated for every store"
          );
        }}
      >
        <div className={cardStyles.dialogSection}>
          <Typography className={cardStyles.dialogSectionTitle}>Activity Logs Visibility</Typography>
          <div className={cardStyles.switchRow} style={{ borderColor: draft.activityLogsVisible ? "#fed7aa" : "#e2e8f0", backgroundColor: draft.activityLogsVisible ? "#fffaf5" : "#f8fafc" }}>
            <span className={cardStyles.switchLabel}>{draft.activityLogsVisible ? "Visible" : "Hidden"}</span>
            <FormControlLabel style={{ margin: 0 }} label="" control={<Switch size="small" color="primary" checked={draft.activityLogsVisible} onChange={(e) => setDraftField("activityLogsVisible")(e.target.checked)} disabled={saving} />} />
          </div>
          {!draft.activityLogsVisible && (
            <Typography variant="caption" style={{ color: "#92400e", display: "block", marginTop: 10, lineHeight: 1.5 }}>
              ⚠️ Hides the Activity Logs nav link and page for every store immediately. SuperAdmin's own Activity Logs tab stays visible so you can still audit and re-enable it.
            </Typography>
          )}

          <Typography className={cardStyles.dialogSectionTitle} style={{ marginTop: 18 }}>
            Log Retention
          </Typography>
          <Typography variant="caption" style={{ color: "#64748b", display: "block", marginBottom: 8, lineHeight: 1.5 }}>
            Log rows older than this are auto-deleted every night (this is what keeps the database from filling up over time - it's shared across every store).
          </Typography>
          <div
            style={{
              background: "#f1f5f9",
              padding: "4px",
              borderRadius: "12px",
              display: "flex",
              width: "100%",
              boxSizing: "border-box",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <ToggleButtonGroup
              value={RETENTION_DAY_PRESETS.includes(Number(draft.programLogRetentionDays)) ? Number(draft.programLogRetentionDays) : null}
              exclusive
              onChange={(e, val) => val != null && setDraftField("programLogRetentionDays")(val)}
              size="small"
              style={{ width: "100%", display: "flex", flexWrap: "wrap" }}
            >
              {RETENTION_DAY_PRESETS.map((d) => (
                <ToggleButton
                  key={d}
                  value={d}
                  disabled={saving || purging}
                  style={{
                    flex: 1,
                    minWidth: 50,
                    borderRadius: "9px",
                    padding: "7px 6px",
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    textTransform: "none",
                    border: "none",
                    backgroundColor: Number(draft.programLogRetentionDays) === d ? "#ffffff" : "transparent",
                    color: Number(draft.programLogRetentionDays) === d ? "#ea580c" : "#64748b",
                    boxShadow: Number(draft.programLogRetentionDays) === d ? "0 2px 6px rgba(0,0,0,0.06)" : "none",
                  }}
                >
                  {d}d
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </div>
          <TextField
            label="Custom (days)"
            type="number"
            size="small"
            variant="outlined"
            value={draft.programLogRetentionDays}
            onChange={(e) => setDraftField("programLogRetentionDays")(e.target.value === "" ? "" : Number(e.target.value))}
            disabled={saving || purging}
            inputProps={{ min: 1, max: 1825 }}
            style={{ marginTop: 10 }}
            fullWidth
          />

          <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px dashed #e2e8f0" }}>
            <Typography className={cardStyles.dialogSectionTitle}>Reclaim Space Now</Typography>
            <Typography variant="caption" style={{ color: "#64748b", display: "block", marginBottom: 10, lineHeight: 1.5 }}>
              The nightly cleanup only deletes what's already past the window above. If the database is already near its limit, purge now instead of waiting for tonight's run.
            </Typography>
            <Button
              variant="outlined"
              disableElevation
              onClick={handlePurgeNow}
              disabled={saving || purging}
              style={{
                textTransform: "none",
                fontWeight: 700,
                color: "#b91c1c",
                borderColor: "#fecaca",
                backgroundColor: "#fef2f2",
              }}
            >
              {purging ? "Purging…" : `Purge logs older than ${Number(draft.programLogRetentionDays) || 30} days now`}
            </Button>
          </div>
        </div>
      </SettingCard>

      <ReportVisibilitySettings />
    </div>

    <Typography variant="h6" style={{ fontWeight: 700, margin: "32px 0 16px" }}>
      Special Features
    </Typography>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20, alignItems: "start" }}>
      <SettingCard
        icon={LockOutlinedIcon}
        iconVariant="cardIconPurple"
        title="Lock Item Price Overrides"
        statOn={settings.lockItemPriceOverrides}
        statOnLabel="Price locked for the bill"
        statOffLabel="Resets to catalog price (current behavior)"
        hint="When ON, a price changed mid-bill stays applied to that item for the rest of the bill - including re-adding it after sending a KOT - instead of resetting to the catalog price. The change also updates the item's master price after the cashier confirms."
        editing={editingCard === "priceLock"}
        onEdit={() => openEdit("priceLock")}
        onCancel={closeEdit}
        saving={saving}
        onSave={() => save({ lockItemPriceOverrides: draft.lockItemPriceOverrides }, "Price override lock updated for every store")}
      >
        <div className={cardStyles.dialogSection}>
          <Typography className={cardStyles.dialogSectionTitle}>Price Override Lock</Typography>
          <div className={cardStyles.switchRow} style={{ borderColor: draft.lockItemPriceOverrides ? "#fed7aa" : "#e2e8f0", backgroundColor: draft.lockItemPriceOverrides ? "#fffaf5" : "#f8fafc" }}>
            <span className={cardStyles.switchLabel}>{draft.lockItemPriceOverrides ? "Price locked for the bill" : "Resets to catalog price"}</span>
            <FormControlLabel style={{ margin: 0 }} label="" control={<Switch size="small" color="primary" checked={draft.lockItemPriceOverrides} onChange={(e) => setDraftField("lockItemPriceOverrides")(e.target.checked)} disabled={saving} />} />
          </div>
        </div>
      </SettingCard>

      <SettingCard
        icon={StraightenOutlinedIcon}
        iconVariant="cardIconTeal"
        title="Minimum Sale Quantity"
        statOn={settings.minSaleQuantityEnabled}
        statOnLabel="Enforced per item"
        statOffLabel="Ignored (current behavior)"
        hint="Enforces each item's own Minimum Sale Quantity (set on the item itself) in the cart - the quantity defaults to it and can't be reduced below it. Items with no minimum set are unaffected either way."
        editing={editingCard === "minQty"}
        onEdit={() => openEdit("minQty")}
        onCancel={closeEdit}
        saving={saving}
        onSave={() => save({ minSaleQuantityEnabled: draft.minSaleQuantityEnabled }, "Minimum sale quantity setting updated for every store")}
      >
        <div className={cardStyles.dialogSection}>
          <Typography className={cardStyles.dialogSectionTitle}>Minimum Sale Quantity</Typography>
          <div className={cardStyles.switchRow} style={{ borderColor: draft.minSaleQuantityEnabled ? "#fed7aa" : "#e2e8f0", backgroundColor: draft.minSaleQuantityEnabled ? "#fffaf5" : "#f8fafc" }}>
            <span className={cardStyles.switchLabel}>{draft.minSaleQuantityEnabled ? "Enforced per item" : "Ignored"}</span>
            <FormControlLabel style={{ margin: 0 }} label="" control={<Switch size="small" color="primary" checked={draft.minSaleQuantityEnabled} onChange={(e) => setDraftField("minSaleQuantityEnabled")(e.target.checked)} disabled={saving} />} />
          </div>
          <Typography variant="caption" style={{ display: "block", marginTop: 8, color: "#64748b" }}>
            The minimum itself is set per item on the Item form (Minimum Sale Quantity) - this switch only controls whether it's actually enforced in the cart.
          </Typography>
        </div>
      </SettingCard>

      <SettingCard
        icon={EventOutlinedIcon}
        iconVariant="cardIconRose"
        title="Back-Date Invoice Creation"
        statOn={settings.backDateInvoiceEntitled}
        statOnLabel={
          Number(settings.backDateInvoiceMaxDays) === 0
            ? "Allowed, no limit"
            : `Allowed, up to ${settings.backDateInvoiceMaxDays}d back`
        }
        statOffLabel="Disabled - always today's date"
        hint="Lets a new invoice be dated in the past, on both the POS checkout screen and the back-office create form."
        editing={editingCard === "backDate"}
        onEdit={() => openEdit("backDate")}
        onCancel={closeEdit}
        saving={saving}
        onSave={() => {
          // 0 is the "No limit" sentinel (same Off=0 convention as the
          // Currency Rounding card below) - only 1-365 needs the range check.
          const days = Number(draft.backDateInvoiceMaxDays);
          if (days !== 0 && (!Number.isInteger(days) || days < 1 || days > 365)) {
            openSnackbar("Max days back must be 0 (no limit) or a whole number between 1 and 365");
            return;
          }
          save({ backDateInvoiceEntitled: draft.backDateInvoiceEntitled, backDateInvoiceMaxDays: days }, "Back-date invoice setting updated for every store");
        }}
      >
        <div className={cardStyles.dialogSection}>
          <Typography className={cardStyles.dialogSectionTitle}>Back-Dating</Typography>
          <div className={cardStyles.switchRow} style={{ borderColor: draft.backDateInvoiceEntitled ? "#fed7aa" : "#e2e8f0", backgroundColor: draft.backDateInvoiceEntitled ? "#fffaf5" : "#f8fafc" }}>
            <span className={cardStyles.switchLabel}>{draft.backDateInvoiceEntitled ? "Allowed" : "Disabled"}</span>
            <FormControlLabel style={{ margin: 0 }} label="" control={<Switch size="small" color="primary" checked={draft.backDateInvoiceEntitled} onChange={(e) => setDraftField("backDateInvoiceEntitled")(e.target.checked)} disabled={saving} />} />
          </div>
          <Collapse in={draft.backDateInvoiceEntitled} timeout={220}>
            <div className={cardStyles.switchRow} style={{ marginTop: 10, borderColor: Number(draft.backDateInvoiceMaxDays) === 0 ? "#fed7aa" : "#e2e8f0", backgroundColor: Number(draft.backDateInvoiceMaxDays) === 0 ? "#fffaf5" : "#f8fafc" }}>
              <span className={cardStyles.switchLabel}>No limit (any past date)</span>
              <FormControlLabel
                style={{ margin: 0 }}
                label=""
                control={
                  <Switch
                    size="small"
                    color="primary"
                    checked={Number(draft.backDateInvoiceMaxDays) === 0}
                    onChange={(e) => setDraftField("backDateInvoiceMaxDays")(e.target.checked ? 0 : 7)}
                    disabled={saving}
                  />
                }
              />
            </div>
            <Collapse in={Number(draft.backDateInvoiceMaxDays) !== 0} timeout={220}>
              <TextField
                label="Max days back"
                type="number"
                size="small"
                variant="outlined"
                value={draft.backDateInvoiceMaxDays}
                onChange={(e) => setDraftField("backDateInvoiceMaxDays")(e.target.value)}
                disabled={saving}
                inputProps={{ min: 1, max: 365 }}
                style={{ marginTop: 10 }}
                fullWidth
              />
            </Collapse>
            <Typography variant="caption" style={{ display: "block", marginTop: 8, color: "#92400e" }}>
              Turning this off removes the date picker from both invoice-creation screens for every store immediately; already-created back-dated invoices are unaffected.
            </Typography>
          </Collapse>
        </div>
      </SettingCard>
    </div>
    </>
  );
};

export default GlobalSettingsTab;
